import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Icon } from '@iconify/react';
import showToast from '@/components/ui/Toast';
import { getVoucher, updateVoucher, VoucherDetailResponse } from '@/services/voucherService';
import { getAdminBasePath } from '@/utils/adminPaths';

const isoToInputDate = (isoStr: string | null | undefined): string => {
  if (!isoStr) return '';
  const d = new Date(isoStr);
  if (isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const EditVoucher: React.FC = () => {
  const { voucherCode } = useParams<{ voucherCode: string }>();
  const navigate = useNavigate();
  const adminBase = getAdminBasePath();

  const [loading, setLoading] = useState<boolean>(true);
  const [submitting, setSubmitting] = useState<boolean>(false);

  // Form states
  const [code, setCode] = useState<string>('');
  const [discountType, setDiscountType] = useState<'percentage' | 'fixed'>('percentage');
  const [discountValue, setDiscountValue] = useState<string>('');
  const [maxDiscountAmount, setMaxDiscountAmount] = useState<string>('');
  const [description, setDescription] = useState<string>('');

  const [validFrom, setValidFrom] = useState<string>('');
  const [validUntil, setValidUntil] = useState<string>('');
  const [usageLimit, setUsageLimit] = useState<string>('');
  const [maxUsagePerUser, setMaxUsagePerUser] = useState<string>('1');

  // Applicable checkboxes
  const [applicableLapangan, setApplicableLapangan] = useState<boolean>(true);
  const [applicableSewa, setApplicableSewa] = useState<boolean>(false);
  const [applicableProduk, setApplicableProduk] = useState<boolean>(false);

  const [minTransactionAmount, setMinTransactionAmount] = useState<string>('0');
  const [isEmailRestricted, setIsEmailRestricted] = useState<boolean>(false);
  const [allowedEmails, setAllowedEmails] = useState<string>('');

  useEffect(() => {
    if (!voucherCode) return;

    const loadData = async () => {
      try {
        setLoading(true);
        const res: VoucherDetailResponse | null = await getVoucher(voucherCode);
        if (res && res.data) {
          const d = res.data;
          setCode(d.voucher_code || voucherCode);
          setDiscountType(d.discount_type === 'fixed' ? 'fixed' : 'percentage');
          setDiscountValue(d.discount_value != null ? String(d.discount_value) : '');
          setMaxDiscountAmount(
            d.max_discount_amount != null && d.max_discount_amount > 0
              ? String(d.max_discount_amount)
              : ''
          );
          setDescription(d.description || '');
          setValidFrom(isoToInputDate(d.valid_from));
          setValidUntil(isoToInputDate(d.valid_until));
          setUsageLimit(d.usage_limit != null && d.usage_limit > 0 ? String(d.usage_limit) : '');
          setMaxUsagePerUser(
            d.max_usage_per_user != null && d.max_usage_per_user > 0
              ? String(d.max_usage_per_user)
              : '1'
          );

          // Parse applicable categories
          let lap = false;
          let sew = false;
          let prod = false;

          if (d.terms_and_conditions) {
            try {
              const parsed = JSON.parse(d.terms_and_conditions);
              if (Array.isArray(parsed?.categories)) {
                lap = parsed.categories.includes('Lapangan');
                sew = parsed.categories.includes('Sewa & Layanan');
                prod = parsed.categories.includes('Produk');
              }
            } catch {
              const s = d.terms_and_conditions.toLowerCase();
              lap = s.includes('arena') || s.includes('lapangan');
              sew = s.includes('service') || s.includes('sewa');
              prod = s.includes('product') || s.includes('produk');
            }
          }

          if (!lap && !sew && !prod) {
            if (d.applicable_for === 'arena') {
              lap = true;
            } else if (d.applicable_for === 'products') {
              prod = true;
            } else {
              lap = true;
              sew = true;
              prod = true;
            }
          }

          setApplicableLapangan(lap);
          setApplicableSewa(sew);
          setApplicableProduk(prod);

          setMinTransactionAmount(
            d.min_transaction_amount != null ? String(d.min_transaction_amount) : '0'
          );
          setIsEmailRestricted(!!d.is_email_restricted);

          if (Array.isArray(d.allowed_emails)) {
            setAllowedEmails(d.allowed_emails.map((item: any) => item.email).join(', '));
          } else {
            setAllowedEmails('');
          }
        } else {
          showToast.error(res?.message || 'Gagal memuat detail voucher');
        }
      } catch (err: any) {
        console.error('Error loading voucher detail:', err);
        showToast.error('Gagal memuat detail voucher');
      } finally {
        setLoading(false);
      }
    };

    void loadData();
  }, [voucherCode]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!voucherCode) return;

    const val = Number(discountValue);
    if (isNaN(val) || val <= 0) {
      showToast.error('Nilai diskon harus lebih dari 0');
      return;
    }

    if (discountType === 'percentage' && val > 100) {
      showToast.error('Diskon persentase maksimal 100%');
      return;
    }

    if (!validFrom) {
      showToast.error('Tanggal berlaku dari wajib diisi');
      return;
    }

    if (!validUntil) {
      showToast.error('Tanggal berlaku sampai wajib diisi');
      return;
    }

    if (new Date(validUntil) < new Date(validFrom)) {
      showToast.error('Tanggal berlaku sampai harus setelah tanggal berlaku dari');
      return;
    }

    if (!applicableLapangan && !applicableSewa && !applicableProduk) {
      showToast.error('Pilih minimal satu kategori berlaku untuk');
      return;
    }

    // Determine backend applicable_for
    let applicable_for = 'both';
    if (applicableLapangan && !applicableSewa && !applicableProduk) {
      applicable_for = 'arena';
    } else if (!applicableLapangan && (applicableSewa || applicableProduk)) {
      applicable_for = 'product';
    } else {
      applicable_for = 'both';
    }

    // Categories list for terms_and_conditions
    const categories: string[] = [];
    if (applicableLapangan) categories.push('Lapangan');
    if (applicableSewa) categories.push('Sewa & Layanan');
    if (applicableProduk) categories.push('Produk');

    const emailList = isEmailRestricted
      ? allowedEmails
          .split(',')
          .map((item) => item.trim().toLowerCase())
          .filter((item) => item.length > 0)
      : [];

    if (isEmailRestricted && emailList.length === 0) {
      showToast.error('Masukkan minimal satu email untuk pembatasan email');
      return;
    }

    const payload: Record<string, any> = {
      voucher_name: code || voucherCode,
      description: description.trim() || null,
      discount_type: discountType,
      discount_value: val,
      max_discount_amount: maxDiscountAmount ? Number(maxDiscountAmount) : 0,
      valid_from: validFrom,
      valid_until: validUntil,
      usage_limit: usageLimit ? Number(usageLimit) : 0,
      max_usage_per_user: maxUsagePerUser ? Number(maxUsagePerUser) : 1,
      applicable_for,
      min_transaction_amount: minTransactionAmount ? Number(minTransactionAmount) : 0,
      is_email_restricted: isEmailRestricted,
      allowed_emails: emailList,
      terms_and_conditions: JSON.stringify({ categories }),
    };

    setSubmitting(true);
    try {
      const res = await updateVoucher(voucherCode, payload);
      if (res.success) {
        showToast.success('Voucher berhasil diperbarui');
        navigate(`${adminBase}/voucher`);
      } else {
        showToast.error(res.message || 'Gagal memperbarui voucher');
      }
    } catch (err: any) {
      console.error(err);
      showToast.error(err?.message || 'Gagal memperbarui voucher');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="p-8 text-center text-gray-400">
        <Icon icon="ph:spinner" className="w-8 h-8 animate-spin mx-auto mb-3 text-[#FC480D]" />
        <p className="text-sm">Memuat detail voucher...</p>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6 max-w-8xl mx-auto">
      <form onSubmit={handleSubmit}>
        {/* ============================================================== */}
        {/* PAGE HEADER: BACK BUTTON, TITLE, ACTIONS                       */}
        {/* ============================================================== */}
        <div className="flex items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => navigate(`${adminBase}/voucher`)}
              className="w-9 h-9 rounded-full border border-gray-200 flex items-center justify-center text-gray-600 hover:bg-gray-100 transition-colors cursor-pointer"
            >
              <Icon icon="ph:arrow-left" className="w-5 h-5" />
            </button>
            <h1 className="text-xl sm:text-2xl font-semibold text-[#0E1726]">Edit Voucher</h1>
          </div>

          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => navigate(`${adminBase}/voucher`)}
              className="text-[#FC480D] font-semibold text-xs sm:text-sm px-4 py-2 hover:bg-orange-50/60 rounded-full transition-colors cursor-pointer"
            >
              Batal
            </button>

            <button
              type="submit"
              disabled={submitting}
              className="inline-flex items-center justify-center gap-2 rounded-full bg-[#FC480D] hover:bg-[#E8490F] disabled:opacity-50 text-white font-semibold text-xs sm:text-sm px-6 py-2.5 shadow-xs transition-colors cursor-pointer"
            >
              {submitting && <Icon icon="ph:spinner" className="w-4 h-4 animate-spin" />}
              <span>Simpan Voucher</span>
            </button>
          </div>
        </div>

        {/* ============================================================== */}
        {/* 2-COLUMN GRID CARDS                                            */}
        {/* ============================================================== */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
          {/* LEFT CARD: INFORMASI VOUCHER */}
          <div className="bg-white rounded-2xl sm:rounded-3xl border border-gray-100 shadow-sm p-6 sm:p-7 space-y-5">
            <h2 className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
              INFORMASI VOUCHER
            </h2>

            {/* KODE VOUCHER */}
            <div>
              <label className="text-xs font-semibold text-gray-700 block mb-1.5">
                Kode voucher
              </label>
              <input
                type="text"
                disabled
                value={code}
                className="w-full rounded-full border border-gray-200 px-4 py-2.5 text-sm text-[#0E1726] bg-gray-50 uppercase font-semibold cursor-not-allowed opacity-80"
              />
              <p className="text-xs text-gray-400 mt-1">Kode voucher unik dan tidak dapat diubah.</p>
            </div>

            {/* TIPE DISKON */}
            <div>
              <label className="text-xs font-semibold text-gray-700 block mb-1.5">
                Tipe diskon
              </label>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => setDiscountType('percentage')}
                  className={`rounded-full px-5 py-2 text-xs sm:text-sm font-semibold transition-colors cursor-pointer ${
                    discountType === 'percentage'
                      ? 'border border-[#FC480D] bg-[#FFF5EE] text-[#FC480D]'
                      : 'border border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  Persentase
                </button>

                <button
                  type="button"
                  onClick={() => setDiscountType('fixed')}
                  className={`rounded-full px-5 py-2 text-xs sm:text-sm font-semibold transition-colors cursor-pointer ${
                    discountType === 'fixed'
                      ? 'border border-[#FC480D] bg-[#FFF5EE] text-[#FC480D]'
                      : 'border border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  Nominal
                </button>
              </div>
            </div>

            {/* NILAI DISKON & MAKS. POTONGAN */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-semibold text-gray-700 block mb-1.5">
                  Nilai diskon <span className="text-red-500">*</span>
                </label>
                <div className="relative flex items-center">
                  {discountType === 'fixed' && (
                    <span className="absolute left-4 text-sm text-gray-400 font-medium">Rp.</span>
                  )}
                  <input
                    type="number"
                    min="0"
                    max={discountType === 'percentage' ? '100' : undefined}
                    required
                    value={discountValue}
                    onChange={(e) => setDiscountValue(e.target.value)}
                    placeholder={discountType === 'percentage' ? '20' : '25000'}
                    className={`w-full rounded-full border border-gray-200 py-2.5 text-sm text-[#0E1726] placeholder:text-gray-400 focus:outline-none focus:border-[#FC480D] transition-colors ${
                      discountType === 'fixed' ? 'pl-11 pr-4' : 'pl-4 pr-10'
                    }`}
                  />
                  {discountType === 'percentage' && (
                    <span className="absolute right-4 text-sm text-gray-400 font-medium">%</span>
                  )}
                </div>
              </div>

              <div>
                <label className="text-xs font-semibold text-gray-700 block mb-1.5">
                  Maks. potongan
                </label>
                <div className="relative flex items-center">
                  <span className="absolute left-4 text-sm text-gray-400 font-medium">Rp.</span>
                  <input
                    type="number"
                    min="0"
                    value={maxDiscountAmount}
                    onChange={(e) => setMaxDiscountAmount(e.target.value)}
                    placeholder="Tanpa batas"
                    className="w-full rounded-full border border-gray-200 pl-11 pr-4 py-2.5 text-sm text-[#0E1726] placeholder:text-gray-400 focus:outline-none focus:border-[#FC480D] transition-colors"
                  />
                </div>
              </div>
            </div>

            {/* DESKRIPSI (OPSIONAL) */}
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-xs font-semibold text-gray-700">Deskripsi (opsional)</label>
                <span className="text-xs text-gray-400">{description.length}/200</span>
              </div>
              <textarea
                maxLength={200}
                rows={3}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Syarat dan ketentuan singkat"
                className="w-full rounded-2xl border border-gray-200 p-3 text-sm text-[#0E1726] placeholder:text-gray-400 focus:outline-none focus:border-[#FC480D] resize-none transition-colors"
              />
            </div>
          </div>

          {/* RIGHT CARD: ATURAN PEMAKAIAN */}
          <div className="bg-white rounded-2xl sm:rounded-3xl border border-gray-100 shadow-sm p-6 sm:p-7 space-y-5">
            <h2 className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
              ATURAN PEMAKAIAN
            </h2>

            {/* BERLAKU DARI & BERLAKU SAMPAI */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-semibold text-gray-700 block mb-1.5">
                  Berlaku dari <span className="text-red-500">*</span>
                </label>
                <div className="relative flex items-center">
                  <input
                    type="date"
                    required
                    value={validFrom}
                    onChange={(e) => setValidFrom(e.target.value)}
                    className="w-full rounded-full border border-gray-200 px-4 py-2.5 text-sm text-[#0E1726] focus:outline-none focus:border-[#FC480D] transition-colors cursor-pointer"
                  />
                </div>
              </div>

              <div>
                <label className="text-xs font-semibold text-gray-700 block mb-1.5">
                  Berlaku sampai <span className="text-red-500">*</span>
                </label>
                <div className="relative flex items-center">
                  <input
                    type="date"
                    required
                    value={validUntil}
                    onChange={(e) => setValidUntil(e.target.value)}
                    className="w-full rounded-full border border-gray-200 px-4 py-2.5 text-sm text-[#0E1726] focus:outline-none focus:border-[#FC480D] transition-colors cursor-pointer"
                  />
                </div>
              </div>
            </div>

            {/* KUOTA PEMAKAIAN & BATAS PER PELANGGAN */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-semibold text-gray-700 block mb-1.5">
                  Kuota pemakaian
                </label>
                <div className="relative flex items-center">
                  <input
                    type="number"
                    min="0"
                    value={usageLimit}
                    onChange={(e) => setUsageLimit(e.target.value)}
                    placeholder="Tanpa batas"
                    className="w-full rounded-full border border-gray-200 pl-4 pr-12 py-2.5 text-sm text-[#0E1726] placeholder:text-gray-400 focus:outline-none focus:border-[#FC480D] transition-colors"
                  />
                  <span className="absolute right-4 text-xs text-gray-400">kali</span>
                </div>
              </div>

              <div>
                <label className="text-xs font-semibold text-gray-700 block mb-1.5">
                  Batas per pelanggan
                </label>
                <div className="relative flex items-center">
                  <input
                    type="number"
                    min="1"
                    value={maxUsagePerUser}
                    onChange={(e) => setMaxUsagePerUser(e.target.value)}
                    placeholder="Tanpa batas"
                    className="w-full rounded-full border border-gray-200 pl-4 pr-12 py-2.5 text-sm text-[#0E1726] placeholder:text-gray-400 focus:outline-none focus:border-[#FC480D] transition-colors"
                  />
                  <span className="absolute right-4 text-xs text-gray-400">kali</span>
                </div>
              </div>
            </div>

            {/* BERLAKU UNTUK */}
            <div>
              <label className="text-xs font-semibold text-gray-700 block mb-2">
                Berlaku untuk <span className="text-red-500">*</span>
              </label>
              <div className="space-y-2.5">
                <label className="flex items-center gap-2.5 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={applicableLapangan}
                    onChange={(e) => setApplicableLapangan(e.target.checked)}
                    className="w-4 h-4 rounded text-[#FC480D] focus:ring-[#FC480D] accent-[#FC480D]"
                  />
                  <span className="text-sm text-gray-800 font-medium">Lapangan</span>
                </label>

                <label className="flex items-center gap-2.5 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={applicableSewa}
                    onChange={(e) => setApplicableSewa(e.target.checked)}
                    className="w-4 h-4 rounded text-[#FC480D] focus:ring-[#FC480D] accent-[#FC480D]"
                  />
                  <span className="text-sm text-gray-800 font-medium">Sewa & Layanan</span>
                </label>

                <label className="flex items-center gap-2.5 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={applicableProduk}
                    onChange={(e) => setApplicableProduk(e.target.checked)}
                    className="w-4 h-4 rounded text-[#FC480D] focus:ring-[#FC480D] accent-[#FC480D]"
                  />
                  <span className="text-sm text-gray-800 font-medium">Produk</span>
                </label>
              </div>
            </div>

            {/* MINIMUM TRANSAKSI */}
            <div>
              <label className="text-xs font-semibold text-gray-700 block mb-1.5">
                Minimum transaksi
              </label>
              <div className="relative flex items-center">
                <span className="absolute left-4 text-sm text-gray-400 font-medium">Rp.</span>
                <input
                  type="number"
                  min="0"
                  value={minTransactionAmount}
                  onChange={(e) => setMinTransactionAmount(e.target.value)}
                  placeholder="0"
                  className="w-full rounded-full border border-gray-200 pl-11 pr-4 py-2.5 text-sm text-[#0E1726] placeholder:text-gray-400 focus:outline-none focus:border-[#FC480D] transition-colors"
                />
              </div>
            </div>

            {/* KHUSUS EMAIL TERTENTU */}
            <div className="pt-2">
              <label className="flex items-center gap-2.5 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={isEmailRestricted}
                  onChange={(e) => setIsEmailRestricted(e.target.checked)}
                  className="w-4 h-4 rounded text-[#FC480D] focus:ring-[#FC480D] accent-[#FC480D]"
                />
                <span className="text-sm text-gray-800 font-medium">Khusus email tertentu</span>
              </label>

              {isEmailRestricted && (
                <div className="mt-3 animate-in fade-in duration-200">
                  <textarea
                    rows={2}
                    value={allowedEmails}
                    onChange={(e) => setAllowedEmails(e.target.value)}
                    placeholder="user1@email.com, user2@email.com"
                    className="w-full rounded-2xl border border-gray-200 p-3 text-sm text-[#0E1726] placeholder:text-gray-400 focus:outline-none focus:border-[#FC480D] resize-none transition-colors"
                  />
                  <p className="text-xs text-gray-400 mt-1">
                    Pisahkan beberapa email dengan tanda koma (,)
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default EditVoucher;
